import { useCallback, useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import { BIOMES, BIOMES_BY_ID } from '@/config/biomes.config';
import { EVENTS, getEventByBiomeId, getEventById } from '@/config/events.config';
import { PETS_BY_ID } from '@/config/pets.config';
import { MERGE_RULES } from '@/config/balance.config';
import { RARITY_ORDER, VARIANT_MULT, VARIANT_ORDER } from '@/domain/types/pet';
import { compareMergeProgress } from '@/domain/economy/petCollectionSort';
import type { PetInstance } from '@/domain/types/pet';
import type { ID, Rarity, Variant } from '@/domain/types/common';

export type PetFilterValue<T extends string> = T | 'all';
export type PetMergeMode = 'all' | 'gold' | 'rainbow';
export type PetOriginFilter =
  | 'all'
  | 'worlds'
  | 'events'
  | `biome:${ID}`
  | `event:${ID}`;
export type PetOriginCounts = ReadonlyMap<PetOriginFilter, number>;
export type PetOriginOptionKind = 'all' | 'worlds' | 'events' | 'biome' | 'event';

export interface PetOriginOption {
  value: PetOriginFilter;
  label: string;
  kind: PetOriginOptionKind;
  biomeId?: ID;
  iconSrc?: string;
  accentColor?: string;
  backgroundTextureKey?: ID;
}

export type PetCollectionSort =
  | 'power'
  | 'rarity'
  | 'variant'
  | 'acquired'
  | 'name'
  | 'priceAsc'
  | 'priceDesc'
  | 'mergeProgress';

export interface PetCollectionFiltersState {
  query: string;
  rarity: PetFilterValue<Rarity>;
  variant: PetFilterValue<Variant>;
  originFilter: PetOriginFilter;
  mergeMode: PetMergeMode;
  sortBy: PetCollectionSort;
  affordableOnly: boolean;
  priceMin: string;
  priceMax: string;
}

export interface PetCollectionItem {
  uid: string;
  defId: string;
  variant: Variant;
  acquiredAt?: number;
  price?: number;
}

export interface MergeProgress {
  count: number;
  required: number;
  label: string;
  ready: boolean;
}

const MERGE_NEAR_THRESHOLD = 5; // pokazuj w filtrze "do rainbow/gold" gdy brakuje ≤5 (tj. od 7/12 gold lub od 5/10 normal)
const BIOME_ORIGIN_PREFIX = 'biome:';
const EVENT_ORIGIN_PREFIX = 'event:';

export const PET_COLLECTION_DEFAULT_FILTERS: PetCollectionFiltersState = {
  query: '',
  rarity: 'all',
  variant: 'all',
  originFilter: 'all',
  mergeMode: 'all',
  sortBy: 'power',
  affordableOnly: false,
  priceMin: '',
  priceMax: '',
};

export const PET_ORIGIN_OPTIONS: PetOriginOption[] = [
  { value: 'all', label: 'Wszystkie', kind: 'all' },
  { value: 'worlds', label: 'Światy', kind: 'worlds' },
  { value: 'events', label: 'Eventy', kind: 'events' },
  ...BIOMES.filter((b) => b.enabled !== false && !b.eventAssetFolder).map((b) => ({
    value: biomeOriginFilterValue(b.id),
    label: b.name.pl,
    kind: 'biome' as const,
    biomeId: b.id,
    accentColor: b.scene.backgroundColor,
    backgroundTextureKey: b.scene.backgroundTextureKey,
  })),
  ...EVENTS.filter((e) => e.petIds.length > 0 || e.biomeId).map((e) => {
    const biome = e.biomeId ? BIOMES_BY_ID[e.biomeId] : undefined;
    return {
      value: eventOriginFilterValue(e.id),
      label: biome?.name.pl ?? e.name.pl,
      kind: 'event' as const,
      biomeId: e.biomeId,
      iconSrc: e.currency?.iconKey ?? '/ui/ui_shell.webp',
      accentColor: biome?.scene.backgroundColor,
      backgroundTextureKey: biome?.scene.backgroundTextureKey,
    };
  }),
];

export function usePetCollectionFilters(
  initial?: Partial<PetCollectionFiltersState>,
): {
  filters: PetCollectionFiltersState;
  setFilters: Dispatch<SetStateAction<PetCollectionFiltersState>>;
  patchFilters: (patch: Partial<PetCollectionFiltersState>) => void;
  resetFilters: () => void;
} {
  const defaults = useMemo(() => ({ ...PET_COLLECTION_DEFAULT_FILTERS, ...initial }), [initial]);
  const [filters, setFilters] = useState<PetCollectionFiltersState>(defaults);
  const patchFilters = useCallback((patch: Partial<PetCollectionFiltersState>): void => {
    setFilters((prev) => ({ ...prev, ...patch }));
  }, []);
  const resetFilters = useCallback((): void => setFilters(defaults), [defaults]);
  return { filters, setFilters, patchFilters, resetFilters };
}

export function normalizePetQuery(value: string): string {
  return value
    .trim()
    .toLocaleLowerCase('pl-PL')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ł/g, 'l');
}

export function petPower(defId: string, variant: Variant): number {
  const def = PETS_BY_ID[defId];
  if (!def) return 0;
  return Math.round(def.basePower * VARIANT_MULT[variant]);
}

export function petBiomeId(defId: string): string | null {
  return PETS_BY_ID[defId]?.origin.biomeId ?? null;
}

export function petEventId(defId: string): string | null {
  const origin = PETS_BY_ID[defId]?.origin;
  if (!origin) return null;
  if (origin.eventId) return origin.eventId;
  return origin.biomeId ? getEventByBiomeId(origin.biomeId)?.id ?? null : null;
}

export function biomeOriginFilterValue(biomeId: ID): PetOriginFilter {
  return `${BIOME_ORIGIN_PREFIX}${biomeId}`;
}

export function eventOriginFilterValue(eventId: ID): PetOriginFilter {
  return `${EVENT_ORIGIN_PREFIX}${eventId}`;
}

export function petOriginFilterValue(defId: string): PetOriginFilter | null {
  const eventId = petEventId(defId);
  if (eventId) return eventOriginFilterValue(eventId);
  const biomeId = petBiomeId(defId);
  return biomeId ? biomeOriginFilterValue(biomeId) : null;
}

export function mergeKey(defId: string, variant: Variant): string {
  return `${defId}::${variant}`;
}

export function requiredForMergeVariant(variant: Variant): number | null {
  if (variant === 'normal') return MERGE_RULES.normalToGold;
  if (variant === 'gold') return MERGE_RULES.goldToRainbow;
  return null;
}

export function variantForMergeMode(mode: PetMergeMode): Variant | null {
  if (mode === 'gold') return 'normal';
  if (mode === 'rainbow') return 'gold';
  return null;
}

export function requiredForMergeMode(mode: PetMergeMode): number | null {
  const variant = variantForMergeMode(mode);
  return variant ? requiredForMergeVariant(variant) : null;
}

export function buildMergeCounts(
  pets: readonly PetInstance[],
  isCounted: (pet: PetInstance) => boolean = defaultMergeCounted,
): Map<string, number> {
  const counts = new Map<string, number>();
  for (const pet of pets) {
    if (!isCounted(pet)) continue;
    const required = requiredForMergeVariant(pet.variant);
    if (required === null) continue;
    const key = mergeKey(pet.defId, pet.variant);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return counts;
}

export function mergeProgressFor(
  item: { defId: string; variant: Variant },
  counts: ReadonlyMap<string, number>,
  countOffset = 0,
): MergeProgress | null {
  const required = requiredForMergeVariant(item.variant);
  if (required === null) return null;
  const count = Math.max(0, (counts.get(mergeKey(item.defId, item.variant)) ?? 0) + countOffset);
  return {
    count,
    required,
    label: `${count}/${required}`,
    ready: count >= required,
  };
}

export function usePetCollectionView<T extends PetCollectionItem>({
  items,
  filters,
  mergeCountPets,
  mergeCounted = defaultMergeCounted,
  mergeCountOffset = defaultMergeCountOffset,
  gems,
}: {
  items: readonly T[];
  filters: PetCollectionFiltersState;
  mergeCountPets: readonly PetInstance[];
  mergeCounted?: (pet: PetInstance) => boolean;
  mergeCountOffset?: (item: T) => number;
  gems?: number;
}): {
  visible: T[];
  mergeCounts: ReadonlyMap<string, number>;
  originCounts: PetOriginCounts;
  mergeProgressForItem: (item: T) => MergeProgress | null;
} {
  const mergeCounts = useMemo(
    () => buildMergeCounts(mergeCountPets, mergeCounted),
    [mergeCountPets, mergeCounted],
  );
  const originCounts = useMemo(() => buildOriginCounts(items), [items]);

  const visible = useMemo(() => {
    const query = normalizePetQuery(filters.query);
    const priceMin = parseOptionalPrice(filters.priceMin);
    const priceMax = parseOptionalPrice(filters.priceMax);
    const mergeVariant = variantForMergeMode(filters.mergeMode);

    return items
      .filter((item) => {
        const def = PETS_BY_ID[item.defId];

        if (query) {
          const eventId = petEventId(item.defId);
          const biomeName = def?.origin.biomeId ? BIOMES_BY_ID[def.origin.biomeId]?.name : undefined;
          const eventName = eventId ? getEventById(eventId)?.name : undefined;
          const haystack = normalizePetQuery(
            [
              def?.name.pl,
              def?.name.en,
              biomeName?.pl,
              biomeName?.en,
              eventName?.pl,
              eventName?.en,
              item.defId,
              ...(def?.tags ?? []),
            ].filter(Boolean).join(' '),
          );
          if (!haystack.includes(query)) return false;
        }
        if (filters.rarity !== 'all' && def?.rarity !== filters.rarity) return false;
        if (filters.variant !== 'all' && item.variant !== filters.variant) return false;
        if (!matchesPetOrigin(item.defId, filters.originFilter)) return false;
        if (mergeVariant && item.variant !== mergeVariant) return false;
        if (mergeVariant && !isNearMergeTarget(item, mergeCounts, filters.mergeMode, mergeCountOffset(item))) {
          return false;
        }
        if (filters.affordableOnly && item.price !== undefined && gems !== undefined && item.price > gems) {
          return false;
        }
        if (priceMin !== null && (item.price === undefined || item.price < priceMin)) return false;
        if (priceMax !== null && (item.price === undefined || item.price > priceMax)) return false;
        return true;
      })
      .sort((a, b) =>
        comparePetCollectionItems(a, b, filters.sortBy, mergeCounts, filters.mergeMode, mergeCountOffset),
      );
  }, [filters, gems, items, mergeCounts, mergeCountOffset]);

  return {
    visible,
    mergeCounts,
    originCounts,
    mergeProgressForItem: (item) => mergeProgressFor(item, mergeCounts, mergeCountOffset(item)),
  };
}

function buildOriginCounts(items: readonly PetCollectionItem[]): PetOriginCounts {
  const counts = new Map<PetOriginFilter, number>();
  const inc = (key: PetOriginFilter): void => {
    counts.set(key, (counts.get(key) ?? 0) + 1);
  };

  for (const item of items) {
    inc('all');
    const def = PETS_BY_ID[item.defId];
    if (!def) continue;

    const eventId = petEventId(item.defId);
    if (eventId) {
      inc('events');
      inc(eventOriginFilterValue(eventId));
      continue;
    }

    inc('worlds');
    if (def.origin.biomeId) {
      inc(biomeOriginFilterValue(def.origin.biomeId));
    }
  }

  return counts;
}

function defaultMergeCounted(pet: PetInstance): boolean {
  return pet.locked !== true && pet.favorite !== true;
}

function defaultMergeCountOffset(): number {
  return 0;
}

function hasEventOrigin(defId: string): boolean {
  return petEventId(defId) !== null;
}

function matchesPetOrigin(defId: string, filterValue: PetOriginFilter): boolean {
  if (filterValue === 'all') return true;
  if (filterValue === 'worlds') return PETS_BY_ID[defId] !== undefined && !hasEventOrigin(defId);
  if (filterValue === 'events') return hasEventOrigin(defId);
  if (filterValue.startsWith(EVENT_ORIGIN_PREFIX)) {
    return petEventId(defId) === filterValue.slice(EVENT_ORIGIN_PREFIX.length);
  }
  if (filterValue.startsWith(BIOME_ORIGIN_PREFIX)) {
    const def = PETS_BY_ID[defId];
    if (!def) return false;
    return def.origin.biomeId === filterValue.slice(BIOME_ORIGIN_PREFIX.length);
  }

  return false;
}

function parseOptionalPrice(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? Math.max(0, Math.floor(n)) : null;
}

function isNearMergeTarget(
  item: { defId: string; variant: Variant },
  counts: ReadonlyMap<string, number>,
  mergeMode: PetMergeMode,
  countOffset = 0,
): boolean {
  const required = requiredForMergeMode(mergeMode);
  if (required === null) return true;
  const count = Math.max(0, (counts.get(mergeKey(item.defId, item.variant)) ?? 0) + countOffset);
  if (count <= 0) return false;
  return required - count <= MERGE_NEAR_THRESHOLD;
}

function comparePetCollectionItems<T extends PetCollectionItem>(
  a: T,
  b: T,
  sortBy: PetCollectionSort,
  mergeCounts: ReadonlyMap<string, number>,
  mergeMode: PetMergeMode,
  mergeCountOffset: (item: T) => number,
): number {
  const defA = PETS_BY_ID[a.defId];
  const defB = PETS_BY_ID[b.defId];

  if (sortBy === 'mergeProgress' || mergeMode !== 'all') {
    const modeRequired = requiredForMergeMode(mergeMode);
    const requiredA = modeRequired ?? requiredForMergeVariant(a.variant);
    const requiredB = modeRequired ?? requiredForMergeVariant(b.variant);
    const countA = (mergeCounts.get(mergeKey(a.defId, a.variant)) ?? 0) + mergeCountOffset(a);
    const countB = (mergeCounts.get(mergeKey(b.defId, b.variant)) ?? 0) + mergeCountOffset(b);
    const progressOrder = compareMergeProgress(countA, requiredA, countB, requiredB);
    if (progressOrder !== 0) return progressOrder;
  }

  switch (sortBy) {
    case 'priceAsc':
      return (a.price ?? Number.MAX_SAFE_INTEGER) - (b.price ?? Number.MAX_SAFE_INTEGER);
    case 'priceDesc':
      return (b.price ?? 0) - (a.price ?? 0);
    case 'rarity':
      return (defB ? RARITY_ORDER[defB.rarity] : 0) - (defA ? RARITY_ORDER[defA.rarity] : 0);
    case 'variant':
      return VARIANT_ORDER[b.variant] - VARIANT_ORDER[a.variant];
    case 'acquired':
      return (b.acquiredAt ?? 0) - (a.acquiredAt ?? 0);
    case 'name':
      return (defA?.name.pl ?? a.defId).localeCompare(defB?.name.pl ?? b.defId, 'pl');
    case 'mergeProgress':
    case 'power':
      return petPower(b.defId, b.variant) - petPower(a.defId, a.variant);
  }
}
